import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { resolveIcon } from "@/components/admin/IconPicker";

export function CategoryPicker({
  open,
  setOpen,
  lang,
  categories,
  activeId,
  onSelect,
}) {
  const active = categories.find((c) => c.id === activeId) ?? categories[0];
  const ActiveIcon = resolveIcon(active.icon);

  const labels = {
    items: lang === "el" ? "πιάτα" : "items",
    tapForAll: lang === "el" ? "πάτησε για όλες" : "tap for all",
    pickCategory: lang === "el" ? "Επίλεξε κατηγορία" : "Pick a category",
  };

  return (
    <div className="relative">
      <button
        data-testid="category-picker-trigger"
        onClick={() => setOpen(!open)}
        className="group flex w-full items-center gap-3 rounded-2xl border border-gold/20 bg-card/40 px-4 py-3 text-left transition-colors hover:border-gold/40 hover:bg-card/60 sm:gap-4 sm:px-5 sm:py-4"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-gold/20 to-gold-muted/10 text-gold ring-1 ring-gold/30 sm:h-11 sm:w-11">
          <ActiveIcon className="h-5 w-5 sm:h-[1.35rem] sm:w-[1.35rem]" strokeWidth={1.6} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-xl italic leading-tight text-foreground sm:text-2xl">
            {lang === "el" ? active.el : active.en}
          </span>
          <span className="mt-0.5 block text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground sm:text-[0.7rem]">
            {active.items.length} {labels.items}
            <span className="mx-1.5 text-gold-muted">·</span>
            {labels.tapForAll}
          </span>
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="shrink-0 text-gold-muted"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="category-picker-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-30 bg-background/60 backdrop-blur-[2px]"
            />
            <motion.div
              data-testid="category-picker-panel"
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-2xl border border-gold/20 bg-card/95 p-3 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-4"
            >
              <div className="mb-3 flex items-center justify-between px-1">
                <p className="text-[0.6rem] uppercase tracking-[0.3em] text-gold-muted sm:text-xs">
                  {labels.pickCategory}
                </p>
                <button
                  data-testid="category-picker-close"
                  onClick={() => setOpen(false)}
                  className="rounded-full p-1 text-muted-foreground transition-colors hover:text-gold"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {categories.map((c, i) => {
                  const Icon = resolveIcon(c.icon);
                  const isActive = c.id === activeId;
                  return (
                    <motion.button
                      key={c.id}
                      data-testid={`category-card-${c.id}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.28,
                        delay: i * 0.025,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      onClick={() => {
                        onSelect(c.id);
                        setOpen(false);
                      }}
                      className={
                        "group relative flex min-h-[88px] flex-col gap-2 rounded-xl border p-3 text-left transition-all sm:gap-3 sm:p-4 " +
                        (isActive
                          ? "border-gold/60 bg-gradient-to-br from-gold/10 to-transparent shadow-[0_0_0_1px_hsl(var(--gold)/0.3)_inset]"
                          : "border-border/60 bg-background/40 hover:border-gold/30 hover:bg-card/80")
                      }
                    >
                      <span
                        className={
                          "flex h-9 w-9 items-center justify-center rounded-lg ring-1 transition-colors sm:h-10 sm:w-10 " +
                          (isActive
                            ? "bg-gold text-background ring-gold"
                            : "bg-card text-gold ring-gold/20 group-hover:ring-gold/40")
                        }
                      >
                        <Icon className="h-4 w-4 sm:h-[1.1rem] sm:w-[1.1rem]" strokeWidth={1.6} />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-display text-base leading-tight text-foreground sm:text-lg">
                          {lang === "el" ? c.el : c.en}
                        </p>
                        <p className="mt-0.5 text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground sm:text-[0.7rem]">
                          {c.items.length} {labels.items}
                        </p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
